import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import ModalHeaderWithActions from './ModalHeaderWithActions';

interface ConfirmDialogProps {
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    show,
    title,
    message,
    onConfirm,
    onCancel
}) => {
    return (
        <Modal show={show} onHide={onCancel} backdrop="static" className="change-modal">
            <ModalHeaderWithActions
                title={title}
                onClose={onCancel}
                onSubmit={onConfirm}
                canSubmit={true}
            />
            <Modal.Body>
                <p>{message}</p>
            </Modal.Body>
        </Modal>
    );
};

// Hook to use the confirm dialog
export const useConfirmDialog = () => {
    const [show, setShow] = useState(false);
    const [resolveFn, setResolveFn] = useState<(value: boolean) => void>();
    const [dialogConfig, setDialogConfig] = useState({
        title: '',
        message: ''
    });

    const confirm = (title: string, message: string): Promise<boolean> => {
        setShow(true);
        setDialogConfig({ title, message });
        return new Promise((resolve) => {
            setResolveFn(() => resolve);
        });
    };

    const handleConfirm = () => {
        setShow(false);
        resolveFn?.(true);
    };

    const handleCancel = () => {
        setShow(false);
        resolveFn?.(false);
    };

    const ConfirmDialogComponent = () => (
        <ConfirmDialog
            show={show}
            title={dialogConfig.title}
            message={dialogConfig.message}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
        />
    );

    return { confirm, ConfirmDialogComponent };
};

// Usage example:
// const { confirm, ConfirmDialogComponent } = useConfirmDialog();
// 
// const handleDelete = async () => {
//   const isConfirmed = await confirm('Confirm Delete', 'Are you sure you want to delete this item?');
//   if (isConfirmed) {
//     // Delete logic here
//   }
// };
//
// In your component's JSX:
// <ConfirmDialogComponent />
// <Button onClick={handleDelete}>Delete</Button>